import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./WalletGrowthMapPage.css";

type Summary = {
  experiences: number;
  evidence: number;
  competencies: number;
  competencyObservations: number;
  achievements: number;
  lifeStamps: number;
};

type Origin = {
  originType: string;
  originResourceId: string;
  label: string;
};

type StoryEvidence = {
  evidenceId: string;
  title: string;
  status: string;
  verifiedAt?: string | null;
  origins: Origin[];
};

type CompetencyObservation = {
  competencyResultId: string;
  score: number;
  observedAt: string;
  indicator?: string | null;
  evaluationResultId: string;
  evaluationName: string;
  confirmedAt?: string | null;
  confirmedNarrative?: string | null;
  evidence: StoryEvidence[];
};

type CompetencyStory = {
  competencyId: string;
  name: string;
  description?: string | null;
  observationCount: number;
  firstScore?: number | null;
  latestScore?: number | null;
  change?: number | null;
  firstObservedAt?: string | null;
  latestObservedAt?: string | null;
  observations: CompetencyObservation[];
};

type ExperienceNode = {
  nodeId: string;
  experienceType: "SCHOOL" | "PERSONAL";
  experienceId: string;
  title: string;
  subtitle?: string | null;
  status: string;
  occurredAt?: string | null;
};

type EvidenceNode = {
  nodeId: string;
  evidenceId: string;
  title: string;
  status: string;
  verifiedAt?: string | null;
  createdAt?: string | null;
};

type CompetencyNode = {
  nodeId: string;
  competencyId: string;
  name: string;
  description?: string | null;
  latestScore?: number | null;
  latestObservedAt?: string | null;
  observationCount: number;
};

type AchievementNode = {
  nodeId: string;
  achievementId: string;
  name: string;
  description?: string | null;
  periodKey?: string | null;
  confirmedAt?: string | null;
};

type LifeStampNode = {
  nodeId: string;
  credentialId: string;
  credentialName: string;
  achievementId: string;
  achievementName: string;
  issuerName: string;
  status: string;
  issuedAt?: string | null;
  expiresAt?: string | null;
  isValid: boolean;
};

type GrowthEdge = {
  edgeId: string;
  fromType: "EXPERIENCE" | "EVIDENCE" | "ACHIEVEMENT";
  fromId: string;
  toType: "EVIDENCE" | "COMPETENCY" | "ACHIEVEMENT" | "LIFE_STAMP";
  toId: string;
  relation: string;
  evaluationResultId?: string;
};

type GrowthMapData = {
  learnerId: string;
  summary: Summary;
  competencyStories: CompetencyStory[];
  map: {
    experiences: ExperienceNode[];
    evidence: EvidenceNode[];
    competencies: CompetencyNode[];
    achievements: AchievementNode[];
    lifeStamps: LifeStampNode[];
    edges: GrowthEdge[];
  };
  trust: {
    competencies: string;
    achievements: string;
    relationshipRule: string;
  };
};

type PortfolioSourceType = "COMPETENCY" | "ACHIEVEMENT" | "LIFE_STAMP";

const RELATION_LABEL: Record<string, string> = {
  EVIDENCE: "경험에서 남은 증빙",
  CONFIRMED_EVALUATION: "확정 평가의 근거",
  ACHIEVEMENT_EVIDENCE: "성취 확인의 근거",
  CREDENTIAL_ISSUANCE: "성취를 바탕으로 발급"
};

function formatDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatScore(value?: number | null) {
  if (value === null || value === undefined) return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1);
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    ACTIVE: "활성",
    VERIFIED: "확인 완료",
    CONFIRMED: "확정",
    COMPLETED: "완료",
    ATTENDED: "참여",
    SELF_REPORTED: "자기기록",
    SUBMITTED: "확인 요청",
    NEEDS_REVISION: "보완 필요",
    REJECTED: "반려",
    DRAFT: "작성 중"
  };
  return labels[status] || status;
}

function relationLabel(relation: string) {
  return RELATION_LABEL[relation] || "연결 기록";
}

function originPath(origin: Origin) {
  if (origin.originType === "PARTICIPATION") return `/experiences/school/${origin.originResourceId}`;
  if (origin.originType === "PERSONAL_EXPERIENCE") return `/experiences/personal/${origin.originResourceId}`;
  return "";
}

function trendText(change?: number | null, count?: number) {
  if (!count || count < 2 || change === null || change === undefined) return "관찰 1회";
  if (change === 0) return "두 관찰 사이 변화 없음";
  return `첫 관찰 대비 ${change > 0 ? "+" : ""}${formatScore(change)}`;
}

export function WalletGrowthMapPage({
  client,
  onNavigate,
  onUseInPortfolio
}: {
  client: PlatformClient;
  onNavigate: (path: string) => void;
  onUseInPortfolio: (sourceType: PortfolioSourceType, sourceId: string) => void;
}) {
  const [data, setData] = useState<GrowthMapData | null>(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState<"MAP" | "STORY">("MAP");
  const [focusNodeId, setFocusNodeId] = useState("");
  const [selectedCompetencyId, setSelectedCompetencyId] = useState("");

  useEffect(() => {
    let active = true;
    client.rpc("wallet_growth_map").then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setError(error.message);
        return;
      }
      const result = data as GrowthMapData;
      setData(result);
      setSelectedCompetencyId((current) =>
        result.competencyStories.some((story) => story.competencyId === current)
          ? current
          : result.competencyStories[0]?.competencyId || ""
      );
    });
    return () => {
      active = false;
    };
  }, [client]);

  const selectedStory = useMemo(
    () => data?.competencyStories.find((story) => story.competencyId === selectedCompetencyId) || null,
    [data, selectedCompetencyId]
  );

  const connectedIds = useMemo(() => {
    if (!data || !focusNodeId) return new Set<string>();
    const ids = new Set<string>([focusNodeId]);
    data.map.edges.forEach((edge) => {
      if (edge.fromId === focusNodeId) ids.add(edge.toId);
      if (edge.toId === focusNodeId) ids.add(edge.fromId);
    });
    return ids;
  }, [data, focusNodeId]);

  const focusEdges = useMemo(
    () => data?.map.edges.filter((edge) => edge.fromId === focusNodeId || edge.toId === focusNodeId) || [],
    [data, focusNodeId]
  );

  if (error) return <p className="error">{error}</p>;
  if (!data) return <p className="muted">성장지도를 연결하는 중…</p>;

  const allNodeCount =
    data.map.experiences.length +
    data.map.evidence.length +
    data.map.competencies.length +
    data.map.achievements.length +
    data.map.lifeStamps.length;

  return (
    <section className="wallet-page growth-map-page">
      <div className="growth-map-hero">
        <div>
          <p className="eyebrow">GROWTH MAP</p>
          <h2>나의 성장지도</h2>
          <p>
            경험이 어떤 증빙으로 남고, 확인된 역량·성취와 Life Stamp로 어떻게 이어졌는지
            실제 연결 기록만 보여줍니다.
          </p>
        </div>
        <div className="growth-map-hero-note">
          <strong>평가가 아니라 연결을 봅니다</strong>
          <span>점수 하나로 나를 규정하지 않고, 언제 어떤 근거에서 확인되었는지 함께 봅니다.</span>
        </div>
      </div>

      <div className="growth-summary-grid">
        <GrowthMetric label="경험" value={data.summary.experiences} />
        <GrowthMetric label="증빙" value={data.summary.evidence} />
        <GrowthMetric label="확인된 역량" value={data.summary.competencies} />
        <GrowthMetric label="성취" value={data.summary.achievements} />
        <GrowthMetric label="Life Stamp" value={data.summary.lifeStamps} />
      </div>

      <div className="growth-mode-switch" role="tablist" aria-label="성장 보기 방식">
        <button className={mode === "MAP" ? "active" : ""} onClick={() => setMode("MAP")}>성장지도</button>
        <button className={mode === "STORY" ? "active" : ""} onClick={() => setMode("STORY")}>역량 이야기</button>
      </div>

      {mode === "MAP" ? (
        <>
          <div className="growth-map-intro">
            <div>
              <strong>카드를 눌러 연결을 확인하세요</strong>
              <span>강조되는 카드는 데이터에서 실제로 연결된 기록입니다.</span>
            </div>
            {focusNodeId && <button className="secondary compact-button" onClick={() => setFocusNodeId("")}>전체 보기</button>}
          </div>

          {allNodeCount === 0 ? (
            <GrowthEmpty
              title="아직 성장지도에 표시할 기록이 없습니다."
              text="경험과 증빙이 쌓이고 교사 확인·평가가 연결되면 이곳에 성장 경로가 나타납니다."
            />
          ) : (
            <div className="growth-stage-scroller">
              <div className="growth-stage-grid">
                <GrowthStage title="경험" caption="무엇을 했는지" count={data.map.experiences.length}>
                  {data.map.experiences.map((item) => (
                    <GrowthNodeCard
                      key={item.nodeId}
                      nodeId={item.nodeId}
                      focusNodeId={focusNodeId}
                      connectedIds={connectedIds}
                      onFocus={setFocusNodeId}
                      eyebrow={item.experienceType === "SCHOOL" ? "학교 경험" : "나의 경험"}
                      title={item.title}
                      meta={`${item.subtitle || ""}${item.occurredAt ? ` · ${formatDate(item.occurredAt)}` : ""}`}
                      badge={statusLabel(item.status)}
                      onOpen={() => onNavigate(
                        `/experiences/${item.experienceType === "SCHOOL" ? "school" : "personal"}/${item.experienceId}`
                      )}
                    />
                  ))}
                </GrowthStage>

                <GrowthStage title="증빙" caption="무엇이 남았는지" count={data.map.evidence.length}>
                  {data.map.evidence.map((item) => (
                    <GrowthNodeCard
                      key={item.nodeId}
                      nodeId={item.nodeId}
                      focusNodeId={focusNodeId}
                      connectedIds={connectedIds}
                      onFocus={setFocusNodeId}
                      eyebrow="증빙"
                      title={item.title}
                      meta={formatDate(item.verifiedAt || item.createdAt)}
                      badge={statusLabel(item.status)}
                    />
                  ))}
                </GrowthStage>

                <GrowthStage title="역량" caption="무엇이 확인됐는지" count={data.map.competencies.length}>
                  {data.map.competencies.map((item) => (
                    <GrowthNodeCard
                      key={item.nodeId}
                      nodeId={item.nodeId}
                      focusNodeId={focusNodeId}
                      connectedIds={connectedIds}
                      onFocus={(id) => {
                        setFocusNodeId(id);
                        setSelectedCompetencyId(item.competencyId);
                      }}
                      eyebrow={`관찰 ${item.observationCount}회`}
                      title={item.name}
                      meta={`최근 기록 ${formatDate(item.latestObservedAt)}`}
                      badge={`${formatScore(item.latestScore)}`}
                      onOpen={() => {
                        setSelectedCompetencyId(item.competencyId);
                        setMode("STORY");
                      }}
                    />
                  ))}
                </GrowthStage>

                <GrowthStage title="성취" caption="무엇을 이루었는지" count={data.map.achievements.length}>
                  {data.map.achievements.map((item) => (
                    <GrowthNodeCard
                      key={item.nodeId}
                      nodeId={item.nodeId}
                      focusNodeId={focusNodeId}
                      connectedIds={connectedIds}
                      onFocus={setFocusNodeId}
                      eyebrow="확인된 성취"
                      title={item.name}
                      meta={formatDate(item.confirmedAt)}
                      badge="성취"
                      actionLabel="문서에 활용"
                      onAction={() => onUseInPortfolio("ACHIEVEMENT", item.achievementId)}
                    />
                  ))}
                </GrowthStage>

                <GrowthStage title="Life Stamp" caption="어떤 기록으로 남았는지" count={data.map.lifeStamps.length}>
                  {data.map.lifeStamps.map((item) => (
                    <GrowthNodeCard
                      key={item.nodeId}
                      nodeId={item.nodeId}
                      focusNodeId={focusNodeId}
                      connectedIds={connectedIds}
                      onFocus={setFocusNodeId}
                      eyebrow={item.issuerName}
                      title={item.credentialName}
                      meta={formatDate(item.issuedAt)}
                      badge={item.isValid ? "유효" : statusLabel(item.status)}
                      actionLabel="Life Stamp 보기"
                      onAction={() => onNavigate("/credentials")}
                    />
                  ))}
                </GrowthStage>
              </div>
            </div>
          )}

          {focusNodeId && (
            <section className="growth-relation-panel">
              <div className="growth-section-heading">
                <div>
                  <p className="eyebrow">ACTUAL LINKS</p>
                  <h3>이 기록과 실제로 연결된 항목</h3>
                </div>
                <span>{focusEdges.length}개 연결</span>
              </div>
              {focusEdges.length ? (
                <div className="growth-relation-list">
                  {focusEdges.map((edge) => (
                    <div key={edge.edgeId} className="growth-relation-row">
                      <strong>{relationLabel(edge.relation)}</strong>
                      <span>{edge.fromId === focusNodeId ? "다음 기록으로 연결" : "이 기록의 근거"}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="muted">현재 데이터에서 직접 연결된 다음 기록은 없습니다.</p>
              )}
            </section>
          )}

          <div className="growth-trust-note">
            <strong>연결 기준</strong>
            <span>
              경험↔증빙, 확정 평가↔역량, 증빙↔성취, 성취↔Life Stamp처럼 데이터에 실제 관계가 있는 경우만 연결합니다.
              역량이 성취를 직접 만들었다고 추정하지 않습니다.
            </span>
          </div>
        </>
      ) : (
        <section className="competency-story-section">
          {data.competencyStories.length === 0 ? (
            <GrowthEmpty
              title="아직 확인된 역량 이야기가 없습니다."
              text="교사가 확인한 평가와 역량 결과가 쌓이면, 어떤 경험과 증빙에서 역량이 확인되었는지 시간순으로 보여드립니다."
            />
          ) : (
            <>
              <div className="competency-story-picker">
                {data.competencyStories.map((story) => (
                  <button
                    key={story.competencyId}
                    className={selectedCompetencyId === story.competencyId ? "active" : ""}
                    onClick={() => setSelectedCompetencyId(story.competencyId)}
                  >
                    <strong>{story.name}</strong>
                    <span>{story.observationCount}회 관찰</span>
                  </button>
                ))}
              </div>

              {selectedStory && (
                <article className="competency-story-card">
                  <div className="competency-story-head">
                    <div>
                      <p className="eyebrow">COMPETENCY STORY</p>
                      <h3>{selectedStory.name}</h3>
                      {selectedStory.description && <p>{selectedStory.description}</p>}
                    </div>
                    <div className="competency-story-score">
                      <span>최근 기록</span>
                      <strong>{formatScore(selectedStory.latestScore)}</strong>
                      <small>{trendText(selectedStory.change, selectedStory.observationCount)}</small>
                    </div>
                  </div>

                  <div className="competency-story-range">
                    <span>첫 관찰 {formatDate(selectedStory.firstObservedAt)}</span>
                    <span>최근 관찰 {formatDate(selectedStory.latestObservedAt)}</span>
                    <span>총 {selectedStory.observationCount}회</span>
                  </div>

                  <div className="competency-observation-list">
                    {selectedStory.observations.map((observation, index) => (
                      <article className="competency-observation" key={observation.competencyResultId}>
                        <div className="competency-observation-index">{selectedStory.observations.length - index}</div>
                        <div className="competency-observation-body">
                          <div className="competency-observation-head">
                            <div>
                              <strong>{observation.evaluationName}</strong>
                              <span>{formatDate(observation.confirmedAt || observation.observedAt)}</span>
                            </div>
                            <span className="competency-observation-score">{formatScore(observation.score)}</span>
                          </div>

                          {observation.indicator && (
                            <p className="competency-indicator">확인 기준 · {observation.indicator}</p>
                          )}

                          {observation.confirmedNarrative && (
                            <div className="competency-narrative">
                              <strong>확인 기록</strong>
                              <p>{observation.confirmedNarrative}</p>
                            </div>
                          )}

                          <div className="competency-evidence-group">
                            <strong>연결된 증빙</strong>
                            {observation.evidence.length ? observation.evidence.map((evidence) => (
                              <div className="competency-evidence-item" key={evidence.evidenceId}>
                                <div>
                                  <span>{evidence.title}</span>
                                  <small>{statusLabel(evidence.status)}{evidence.verifiedAt ? ` · ${formatDate(evidence.verifiedAt)}` : ""}</small>
                                </div>
                                <div className="competency-origin-chips">
                                  {evidence.origins.map((origin) => {
                                    const path = originPath(origin);
                                    return path ? (
                                      <button key={`${origin.originType}-${origin.originResourceId}`} onClick={() => onNavigate(path)}>
                                        {origin.label}
                                      </button>
                                    ) : (
                                      <span key={`${origin.originType}-${origin.originResourceId}`}>{origin.label}</span>
                                    );
                                  })}
                                </div>
                              </div>
                            )) : <span className="muted">이 관찰에 연결된 증빙이 없습니다.</span>}
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>

                  {selectedStory.observations[0]?.competencyResultId && (
                    <div className="competency-story-actions">
                      <button
                        className="primary"
                        onClick={() => onUseInPortfolio("COMPETENCY", selectedStory.observations[0].competencyResultId)}
                      >
                        이 역량 기록을 문서에 활용
                      </button>
                    </div>
                  )}
                </article>
              )}
            </>
          )}

          <div className="growth-trust-note">
            <strong>역량 이야기는 확정된 기록만 사용합니다</strong>
            <span>
              점수의 높고 낮음을 성격이나 가능성으로 단정하지 않습니다. 여러 시점의 관찰과 그 근거를 함께 보여주는 성장 기록입니다.
            </span>
          </div>
        </section>
      )}
    </section>
  );
}

function GrowthMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="growth-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function GrowthStage({
  title,
  caption,
  count,
  children
}: {
  title: string;
  caption: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="growth-stage">
      <header>
        <div>
          <span>{caption}</span>
          <strong>{title}</strong>
        </div>
        <em>{count}</em>
      </header>
      <div className="growth-stage-body">
        {count ? children : <div className="growth-stage-empty">아직 연결된 기록이 없습니다.</div>}
      </div>
    </section>
  );
}

function GrowthNodeCard({
  nodeId,
  focusNodeId,
  connectedIds,
  onFocus,
  eyebrow,
  title,
  meta,
  badge,
  onOpen,
  actionLabel,
  onAction
}: {
  nodeId: string;
  focusNodeId: string;
  connectedIds: Set<string>;
  onFocus: (id: string) => void;
  eyebrow: string;
  title: string;
  meta: string;
  badge: string;
  onOpen?: () => void;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const focused = focusNodeId === nodeId;
  const connected = Boolean(focusNodeId && connectedIds.has(nodeId));
  const dimmed = Boolean(focusNodeId && !connectedIds.has(nodeId));

  return (
    <article
      className={[
        "growth-node-card",
        focused ? "focused" : "",
        connected && !focused ? "connected" : "",
        dimmed ? "dimmed" : ""
      ].filter(Boolean).join(" ")}
    >
      <button className="growth-node-focus" onClick={() => onFocus(focused ? "" : nodeId)}>
        <span>{eyebrow}</span>
        <strong>{title}</strong>
        <small>{meta}</small>
        <em>{badge}</em>
      </button>
      {(onOpen || onAction) && (
        <div className="growth-node-actions">
          {onOpen && <button onClick={onOpen}>열기</button>}
          {onAction && actionLabel && <button onClick={onAction}>{actionLabel}</button>}
        </div>
      )}
    </article>
  );
}

function GrowthEmpty({ title, text }: { title: string; text: string }) {
  return (
    <div className="growth-empty">
      <strong>{title}</strong>
      <p>{text}</p>
    </div>
  );
}
